// Admin's own profile — view info, edit name/email, change password.
import { useEffect, useState } from "react";
import api, { extractError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { X, User, Save, KeyRound, ShieldCheck, LogOut } from "lucide-react";
import PasswordInput from "@/components/PasswordInput";

export default function AdminProfileModal({ onClose }) {
  const { user, refresh, logout } = useAuth();
  const [form, setForm] = useState({ name: "", email: "" });
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");

  useEffect(() => {
    if (user) setForm({ name: user.name || "", email: user.email || "" });
  }, [user]);

  const saveInfo = async () => {
    setBusy(true); setErr(""); setOk("");
    try {
      await api.patch("/auth/me", { name: form.name.trim(), email: form.email.trim() });
      await refresh();
      setOk("تم حفظ المعلومات");
      setTimeout(() => setOk(""), 2000);
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  const changePw = async () => {
    setErr(""); setOk("");
    if (!pw.current || !pw.next) return setErr("املأ الحقول");
    if (pw.next.length < 6) return setErr("كلمة المرور الجديدة قصيرة (6 أحرف على الأقل)");
    if (pw.next !== pw.confirm) return setErr("تأكيد كلمة المرور غير متطابق");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "", confirm: "" });
      setOk("تم تحديث كلمة المرور بنجاح");
      setTimeout(() => setOk(""), 3000);
    } catch (e) { setErr(extractError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
         onClick={onClose} data-testid="admin-profile-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white w-full sm:max-w-lg rounded-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 text-white flex items-center justify-between"
             style={{background: "linear-gradient(135deg, #1E3A8A 0%, #2547D0 100%)"}}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center font-black text-xl"
                 style={{fontFamily:"Cairo"}}>
              {(user?.name || "A")[0]}
            </div>
            <div>
              <div className="text-xs opacity-90 flex items-center gap-1">
                <ShieldCheck size={11}/> مدير النظام
              </div>
              <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>{user?.name}</div>
              <div className="text-[11px] opacity-80" dir="ltr">{user?.email || user?.phone}</div>
            </div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                  data-testid="admin-profile-close">
            <X size={16}/>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {ok && <div className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-2 rounded-lg text-sm" data-testid="admin-profile-ok">{ok}</div>}
          {err && <div className="bg-red-50 text-red-600 border border-red-200 px-3 py-2 rounded-lg text-sm" data-testid="admin-profile-err">{err}</div>}

          {/* Personal info */}
          <section>
            <h3 className="font-black text-sm flex items-center gap-2 mb-3" style={{fontFamily:"Cairo"}}>
              <User size={14}/> المعلومات الشخصية
            </h3>
            <div className="space-y-2">
              <div>
                <label className="text-xs font-bold text-[var(--text-secondary)]">الاسم</label>
                <input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}
                       className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-white focus:border-primary-brand focus:outline-none text-sm"
                       data-testid="admin-profile-name"/>
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-secondary)]">البريد الإلكتروني</label>
                <input value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
                       className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-white focus:border-primary-brand focus:outline-none text-sm" dir="ltr"
                       data-testid="admin-profile-email"/>
              </div>
              <div>
                <label className="text-xs font-bold text-[var(--text-secondary)]">الهاتف</label>
                <input value={user?.phone || ""} disabled
                       className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] text-sm text-[var(--text-secondary)]" dir="ltr"/>
                <div className="text-[10px] text-[var(--text-secondary)] mt-0.5">لتغيير الهاتف تواصل مع مدير آخر</div>
              </div>
              <button onClick={saveInfo} disabled={busy}
                      className="w-full py-2.5 rounded-xl bg-primary-brand text-white font-black text-sm flex items-center justify-center gap-1.5 hover:opacity-90 disabled:opacity-60"
                      data-testid="admin-profile-save">
                <Save size={14}/> {busy ? "جاري الحفظ..." : "حفظ التغييرات"}
              </button>
            </div>
          </section>

          {/* Password */}
          <section className="pt-4 border-t border-[var(--border)]">
            <h3 className="font-black text-sm flex items-center gap-2 mb-3" style={{fontFamily:"Cairo"}}>
              <KeyRound size={14}/> تغيير كلمة المرور
            </h3>
            <div className="space-y-2">
              <PasswordInput placeholder="كلمة المرور الحالية"
                     value={pw.current} onChange={(e) => setPw({...pw, current: e.target.value})}
                     inputClassName="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-white focus:border-primary-brand focus:outline-none text-sm"
                     testId="admin-pw-current"/>
              <PasswordInput placeholder="كلمة المرور الجديدة"
                     value={pw.next} onChange={(e) => setPw({...pw, next: e.target.value})}
                     inputClassName="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-white focus:border-primary-brand focus:outline-none text-sm"
                     testId="admin-pw-new"/>
              <PasswordInput placeholder="تأكيد كلمة المرور الجديدة"
                     value={pw.confirm} onChange={(e) => setPw({...pw, confirm: e.target.value})}
                     inputClassName="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-white focus:border-primary-brand focus:outline-none text-sm"
                     testId="admin-pw-confirm"/>
              <button onClick={changePw} disabled={busy}
                      className="w-full py-2.5 rounded-xl bg-[var(--secondary)] text-white font-black text-sm flex items-center justify-center gap-1.5 hover:opacity-90 disabled:opacity-60"
                      data-testid="admin-pw-save">
                <KeyRound size={14}/> {busy ? "جاري..." : "تحديث كلمة المرور"}
              </button>
            </div>
          </section>

          {/* Logout */}
          <section className="pt-4 border-t border-[var(--border)]">
            <button onClick={logout}
                    className="w-full py-2.5 rounded-xl bg-red-50 text-red-600 border border-red-200 font-black text-sm flex items-center justify-center gap-1.5 hover:bg-red-100"
                    data-testid="admin-profile-logout">
              <LogOut size={14}/> تسجيل الخروج من الحساب
            </button>
          </section>
        </div>
      </div>
    </div>
  );
}
